import type { ImportComputation, ImportComputationKind } from '@testflow/shared';
import { useTranslation } from 'react-i18next';
import { translateImportError } from './import-errors.js';

// #73: Zeigt eine laufende Berechnung mit Fortschritt oder ihren Abbruch mit „Neu starten".
// Bei Budget-Stopp endet ein Lauf als `done` mit done < total — done/total ist daher nur ein
// Fortschritt, keine Erfolgsquote; nach `done` zählt die Vorschau selbst.
//
// #73 (Review M3): Start- und Endzeit stehen dabei, damit man einen hängenden oder längst
// abgebrochenen Lauf erkennt („läuft seit 08:05“ um 11 Uhr). Uhrzeit in der i18n-Sprache, nicht in
// der Browsersprache (Muster wie das Quell-Import-Datum im Upload-Schritt).
function clockTime(iso: string | null, lang: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
}

export function ComputationStatus({
  computation,
  kind,
  onRestart,
}: {
  computation: ImportComputation;
  kind: ImportComputationKind;
  onRestart: () => void;
}) {
  const { t, i18n } = useTranslation();

  if (computation.state === 'failed' || computation.state === 'interrupted') {
    const endedAt = clockTime(computation.finished_at ?? computation.started_at, i18n.language);
    return (
      <div className="max-w-xl">
        <p role="alert" className="text-xs text-status-fail">
          {translateImportError(t, computation.error ?? 'import_computation_failed')}
        </p>
        {endedAt && (
          <p className="mt-1 text-[11px] text-text-muted">
            {t(`import.computation.endedAt.${computation.state}`, { time: endedAt })}
          </p>
        )}
        <button
          type="button"
          onClick={onRestart}
          className="mt-4 rounded-btn bg-brand-accent px-4 py-2 text-sm font-medium text-white"
        >
          {t('import.computation.restart')}
        </button>
      </div>
    );
  }

  const { done, total } = computation;
  const since = clockTime(computation.started_at, i18n.language);
  return (
    <div className="max-w-xl text-text-muted">
      <p className="text-sm text-text-body">
        {total === 0
          ? t('import.computation.preparing')
          : t(`import.computation.running.${kind}`, { done, total })}
      </p>
      <progress
        {...(total > 0 ? { value: done, max: total } : {})}
        aria-label={t('import.computation.progressLabel')}
        className="mt-2 h-2 w-full"
      />
      {since && (
        <p className="mt-1 text-[11px]">{t('import.computation.since', { time: since })}</p>
      )}
      {/* #73 (Review I2): Nur die Vorschau wird nach dem Wegnavigieren wieder aufgenommen; die
          Blatt-Erkennung im Upload-Schritt bewusst nicht (import-page.tsx) — dort kein Versprechen. */}
      <p className="mt-2 text-[11px]">
        {t(
          kind === 'preview' ? 'import.computation.backgroundHint' : 'import.computation.stayHint',
        )}
      </p>
    </div>
  );
}
